export const STUDIO_API_PREFIX = '/studio-api';

export const StudioBackendRoutes = {
  HEALTH: '/health',
  API_HEALTH: `${STUDIO_API_PREFIX}/health`,
  AGENT_STATUS: `${STUDIO_API_PREFIX}/agent-status`,
  PROMPT_ACTIVITY: `${STUDIO_API_PREFIX}/prompt-activity`,
  AUTH_LOGIN: `${STUDIO_API_PREFIX}/auth/login`,
  AUTH_CALLBACK: `${STUDIO_API_PREFIX}/auth/callback`,
  AUTH_LOGOUT: `${STUDIO_API_PREFIX}/auth/logout`,
  AUTH_SESSION: `${STUDIO_API_PREFIX}/auth/session`,
  AUTH_PREVIEW_CHECK: `${STUDIO_API_PREFIX}/auth/preview-check`,
  AUTH_APP_PERMISSIONS: `${STUDIO_API_PREFIX}/auth/app-permissions`,
  SESSIONS: `${STUDIO_API_PREFIX}/sessions`,
  SCRATCH_APP_SESSIONS: `${STUDIO_API_PREFIX}/sessions/scratch-app`,
  SCRATCH_APP: (slug: string): string => `${STUDIO_API_PREFIX}/scratch-apps/${slug}`,
  SESSION: (sessionId: string): string => `${STUDIO_API_PREFIX}/sessions/${sessionId}`,
  SESSION_SHARING: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/sharing`,
  SESSION_MESSAGES: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/messages`,
  SESSION_PREVIEW: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/preview`,
  SESSION_TOUCH: (sessionId: string): string => `${STUDIO_API_PREFIX}/sessions/${sessionId}/touch`,
  SESSION_MERGE_REQUEST: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/merge-request`,
  SESSION_SYNC_BASE: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/sync-base`,
  SESSION_ABANDON: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/abandon`,
  SESSION_CANCEL: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/cancel`,
  HOST_PREVIEW: `${STUDIO_API_PREFIX}/host-preview`,
  HOST_PREVIEW_SYNC: `${STUDIO_API_PREFIX}/host-preview/sync`,
  TARGETS: `${STUDIO_API_PREFIX}/targets`,
  GITLAB_WEBHOOK: `${STUDIO_API_PREFIX}/gitlab/webhook`,
  AUTOMATIONS: `${STUDIO_API_PREFIX}/automations`,
  AUTOMATION: (id: string): string => `${STUDIO_API_PREFIX}/automations/${id}`,
  AUTOMATION_ENABLE: (id: string): string => `${STUDIO_API_PREFIX}/automations/${id}/enable`,
  AUTOMATION_RUN: (id: string): string => `${STUDIO_API_PREFIX}/automations/${id}/run`,
  FAVORITES: `${STUDIO_API_PREFIX}/favorites`,
  FAVORITE: (sessionId: string): string => `${STUDIO_API_PREFIX}/favorites/${sessionId}`,
  DEPLOYMENTS: `${STUDIO_API_PREFIX}/deployments`,
  DEPLOYMENT: (slug: string): string => `${STUDIO_API_PREFIX}/deployments/${slug}`,
  DEPLOYMENT_COMMITS: (slug: string): string => `${STUDIO_API_PREFIX}/deployments/${slug}/commits`,
  DEPLOYMENT_REVERT: (slug: string): string => `${STUDIO_API_PREFIX}/deployments/${slug}/revert`,
  LAST_INDEPENDENT_SESSION: `${STUDIO_API_PREFIX}/sessions/last-independent`,
  SESSION_DEPLOY: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/deploy`,
  SESSION_THUMBNAIL: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/thumbnail`,
  SESSION_BROWSER_PREVIEW: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/browser-preview`,
  SESSION_BROWSER_RESULTS: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/browser-results`,
  SESSION_TEACH_START: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/teach/start`,
  SESSION_TEACH_STOP: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/teach/stop`,
  SESSION_TEACH_STATUS: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/teach/status`,
  SESSION_TEACH_PROMOTE: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/teach/promote`,
  SESSION_TEACH_DISCARD: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/teach/discard`,
  SESSION_TEACH_FRAME: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/teach/frame`,
  SESSION_TEACH_INPUT: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/teach/input`,
  SESSION_TEACH_VIEWER: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/teach/viewer`,
  SESSION_SOURCES_CONNECT: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/sources/connect`,
  SESSION_SOURCES_SKIP: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/sources/skip`,
  SESSION_SOURCES_STATUS: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/sources/status`,
  SESSION_SOURCES_FRAME: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/sources/frame`,
  SESSION_SOURCES_INPUT: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/sources/input`,
  SESSION_SOURCES_VIEWER: (sessionId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/sources/viewer`,
  SESSION_ATTACHMENT: (sessionId: string, attachmentId: string): string =>
    `${STUDIO_API_PREFIX}/sessions/${sessionId}/attachments/${attachmentId}`,
} as const;

export const StudioPageRoutes = {
  ROOT: '/',
  APPS: '/apps',
  /** Legacy host-preview path; the UI redirects to a per-target URL. */
  HOME: '/home',
  AUTOMATIONS: '/automations',
  SESSION: (sessionId: string): string => `/sessions/${sessionId}`,
} as const;

export type StudioRouteMatch = {
  readonly params: readonly string[];
};

export type StudioRouteQuery = Readonly<Record<string, string | undefined>>;

export function matchStudioRoute(pathname: string, pattern: string): StudioRouteMatch | undefined {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/:(\w+)/g, '([^/]+)');
  const match = new RegExp(`^${escaped}$`).exec(pathname);
  if (match === null) {
    return undefined;
  }
  return { params: match.slice(1) };
}

/** Append optional query params to a shared studio path (page or API). */
export function studioRouteWithQuery(path: string, query: StudioRouteQuery): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined) {
      params.set(key, value);
    }
  });
  const encoded = params.toString();
  return encoded.length === 0 ? path : `${path}?${encoded}`;
}
